/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

import Quill from "quill";
import "quill/dist/quill.snow.css";

modulum("RichTextWidget", ["TextEditWidget", "WidgetFactory"], function (context, cls) {
  /**
   * TextEdit widget.
   * @class RichTextWidget
   * @memberOf classes
   * @extends classes.TextEditWidget
   */
  cls.RichTextWidget = context.oo.Class(cls.TextEditWidget, function ($super) {
    return /** @lends classes.RichTextWidget.prototype */ {
      __name: "RichTextWidget",
      /**
       * @type {?string}
       */
      _richtextPath: null,

      _richTextProperties: null,

      _hasHTMLContent: true,

      // Don't use the forced white bg color for this widget
      _ignoreBackgroundColor: true,

      // QuillJS instance
      _quill: null,
      /** @type {Element} **/
      _editorElement: null,
      /** @type {Element} **/
      _toolbarElement: null,
      /** @type {?HTMLElement} */
      _rewriteActionsBtn: null,
      /** @type {Boolean} */
      _showEditToolBox: true,

      /** @type {boolean} */
      _rewriteInProgress: false,
      /** @type {boolean} */
      _stopRewriteRequested: false,
      /** @type {{text:string, range:{index:number,length:number}|null, rewriteOptions:Object}|null} */
      _aiPendingRewrite: null,
      /** @type {classes.FloatingBannerWidget} */
      _aiBanner: null,
      /** @type {classes.ChoiceDropDownWidget} */
      _rewriteActionsDropDown: null,

      /** @type {{index:number,length:number}|null} */
      _rewriteTargetRange: null,

      /** @type {boolean} */
      _aiWritingAssistant: true,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this.removeClass("gbc_TextEditWidget");
        this._editorElement = this._element.getElementsByClassName("quill-editor")[0];
        this._inputElement = this._editorElement;
        this._showEditToolBox = !this.isInTable();
      },

      /**
       * Shares WebComponent's `.hidden` SCSS (`display:block` off-screen) to
       * keep the Quill editor state, so it needs `inert`, not `display:none`.
       * @inheritDoc
       */
      hidesOutOfView: function () {
        return true;
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._rewriteInProgress = false;
        this._stopRewriteRequested = false;
        this._aiPendingRewrite = null;

        if (this._aiStopItem) {
          this._aiStopItem.destroy();
          this._aiStopItem = null;
        }
        if (this._aiApplyItem) {
          this._aiApplyItem.destroy();
          this._aiApplyItem = null;
        }
        if (this._aiRefreshItem) {
          this._aiRefreshItem.destroy();
          this._aiRefreshItem = null;
        }
        if (this._aiCancelItem) {
          this._aiCancelItem.destroy();
          this._aiCancelItem = null;
        }
        if (this._aiConfirmTextEdit) {
          this._aiConfirmTextEdit.destroy();
          this._aiConfirmTextEdit = null;
        }

        this._aiLoadingContent = null;
        this._aiConfirmHeaderContent = null;
        this._aiConfirmContent = null;

        if (this._aiBanner) {
          this._aiBanner.destroy();
          this._aiBanner = null;
        }

        if (this._rewriteActionsDropDown) {
          this._rewriteActionsDropDown.destroyChildren();
          this._rewriteActionsDropDown.destroy();
          this._rewriteActionsDropDown = null;
        }

        $super.destroy.call(this);
      },

      /**
       * This protected method is called for each widget when they are added in DOM.
       * Init quill once in DOM
       * @protected
       */
      _setDOMAttachedOrDetached: function () {
        $super._setDOMAttachedOrDetached.call(this);
        this._initQuill();
      },

      /**
       * Start Quill library
       * @private
       */
      _initQuill: function () {
        this._ensureAIRewriteTargetAttributor();
        const toolbarOptions = [
          ["bold", "italic", "underline"], // toggled buttons
          [{ align: [] }],
          [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
          [{ list: "ordered" }, { list: "bullet" }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }]
        ];

        if (gbc.AIService.isEnabled() && this._aiWritingAssistant) {
          toolbarOptions.push(["rewrite", "rewriteactions"]);
        }

        this._quill = new Quill(this._editorElement, {
          theme: "snow",
          modules: {
            toolbar: {
              container: toolbarOptions,
              handlers: {
                rewrite: this._handleRewrite.bind(this),
                rewriteactions: this._handleRewriteActions.bind(this)
              }
            }
          }
        });
        this._toolbarElement = this._element.getElementsByClassName("ql-toolbar")[0];
        // Cache the AI rewrite-actions button once: Quill builds it inside the toolbar during
        // its constructor. Falls back to null when AI is disabled (button not added).
        this._rewriteActionsBtn = this._toolbarElement?.querySelector(".ql-rewriteactions") ?? null;

        this._quill.enable(true);
        this.setEnabled(true);
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        // Stop keys to be handled by Quill editor
        let keyProcessed = false;

        if (this._aiBanner && this._aiBanner.isVisible() && keyString === "esc") {
          if (this._aiBanner.hasClass("gbc_aiRewriteConfirmState")) {
            this._cancelPendingRewrite();
          } else {
            this._stopRewrite();
          }
          keyProcessed = true;
        }

        if (keyProcessed) {
          return true;
        }
        return keyString === "tab" || keyString === "return";
      },

      manageKeyDown: function (keyString, domKeyEvent, repeat) {
        // do nothing
      },

      _initListeners: function () {
        // do nothing
      },
      /**
       * Make the textedit content return the new line breaking the word or not
       * @param {string} format - css value
       * @publicdoc
       */
      setWrapPolicy: function (format) {
        // do nothing
      },

      /**
       * Set number of editable rows inside the textedit widget
       * @param {number} rows - number of rows
       * @publicdoc
       */
      setRows: function (rows) {
        // do nothing
      },

      /**
       * Replace default textarea element used to display text with a html element which can displays HTML
       * @param {HTMLElement} jcontrol - div element
       * @publicdoc
       */
      setHtmlControl: function (jcontrol) {
        // do nothing
      },

      /**
       * Enables or disables the AI rewriter for this richtext widget
       * @param {boolean} value - true to enable the AI rewriter, false to disable it
       */
      setAiWritingAssistant: function (value) {
        this._aiWritingAssistant = value;
        if (this._toolbarElement) {
          if (!gbc.AIService.isEnabled() || !value) {
            this._toolbarElement.querySelector(".ql-rewrite")?.closest(".ql-formats").style.setProperty("display", "none");
          }
        }
      },

      /**
       * Handle visibility of the RichText toolbox
       * @param {Boolean} show True to show the toolbox
       */
      showEditToolBox: function (show) {
        this._showEditToolBox = this.isInTable() ? false : show;
        this.updateToolBoxElement();
      },

      /**
       * Add or remove the toolbox HTMLElement
       * depending on the widget configuration and runtime state.
       * The toolbar is only shown when the user can actually edit:
       * the field must be enabled, not read-only, and not NOENTRY.
       */
      updateToolBoxElement: function () {
        if (this._toolbarElement) {
          const editable = this.isEnabled() && !this.isReadOnly() && !this._notEditable;
          if (this._showEditToolBox && editable) {
            this.getElement().insertBefore(this._toolbarElement, this._editorElement);
          } else {
            // Keep keyboard focus anchored on the field if it was inside the toolbar
            if (this._toolbarElement.contains(document.activeElement)) {
              this.setFocus();
            }
            this._toolbarElement.remove();
          }
        }
      },

      /**
       * @inheritDoc
       */
      setReadOnly: function (readonly) {
        $super.setReadOnly.call(this, readonly);
        this._quill?.enable(!readonly);
        this.updateToolBoxElement();
      },

      /**
       * Overridden to avoid inheritance errors
       */
      _setInputReadOnly: function (readonly) {
        // do nothing
      },

      /**
       * Overridden to avoid inheritance errors
       */
      _insertChar: function (c) {
        // do nothing
      },

      /**
       * @inheritDoc
       */
      setEnabled: function (enabled, noSelectionUpdate) {
        $super.setEnabled.call(this, enabled, noSelectionUpdate);
        // Enable or disable Editor
        this._quill?.enable(enabled);
        this.updateToolBoxElement();
      },

      /**
       * NotEditable allows cursor moving, but not a value change
       * @param {boolean} notEditable - true to set the edit part as read-only
       */
      setNotEditable: function (notEditable) {
        this._notEditable = notEditable;
        this._quill?.enable(!notEditable);
        this.updateToolBoxElement();
      },

      /**
       * @inheritDoc
       */
      setValue: function (value, fromVM = false, cursorPosition = null) {
        if (value === "") {
          value = null;
        }
        if (this._quill && this.getValue() !== value) {
          if (value === null) {
            this._quill.setText("");
          } else {
            this._quill.clipboard.dangerouslyPasteHTML(value);
          }
        }
        this._value = value;
      },

      /**
       * @inheritDoc
       */
      getValue: function () {
        return this._quill?.getSemanticHTML(); // new in Quill 2 : no need for parser
      },

      /**
       * @inheritDoc
       */
      setFocus: function (fromMouse = false, preventScroll = false) {
        $super.setFocus.call(this, fromMouse, preventScroll);
        this._quill?.focus({ preventScroll: true });
      },

      /**
       * Cursors are not handled in case of richtext because of HTML formatting
       * Note that we keep it here to handle the hasCursor() method correctly
       */
      hasCursors: function () {
        return false;
      },

      /**
       * Run AI rewrite on current selection or full content
       * @private
       * @async
       * @param {?Object} rewriteOptions
       * @returns {Promise<void>}
       */
      _handleRewrite: async function (rewriteOptions, forcedRange = null) {
        if (this._rewriteInProgress) {
          return;
        }
        this._clearRewriteTargetDecoration();
        this._rewriteInProgress = true;
        this._stopRewriteRequested = false;

        let range = this._quill.getSelection();
        if (forcedRange && forcedRange.length > 0) {
          range = forcedRange;
        }
        const hasSelection = range && range.length > 0;

        if (hasSelection) {
          this._rewriteTargetRange = { index: range.index, length: range.length };
          this._quill.formatText(range.index, range.length, "aiRewriteTarget", "on", "silent");
          const endIndex = range.index + range.length;
          this._quill.setSelection(endIndex, 0, "silent");
          this._quill.blur();
        } else {
          const fullLength = Math.max(this._quill.getLength() - 1, 0);
          this._rewriteTargetRange = fullLength > 0 ? { index: 0, length: fullLength } : null;
          if (this._rewriteTargetRange) {
            this._quill.formatText(this._rewriteTargetRange.index, this._rewriteTargetRange.length, "aiRewriteTarget", "on", "silent");
          }
        }
        this.addClasses("gbc_aiLoading");

        this._setAIBannerLoading();

        try {
          let text = "";
          if (hasSelection) {
            text = this._quill.getText(range.index, range.length);
          } else {
            text = this._quill.getText();
          }

          const callOptions = {
            contextWidget: this,
            ...rewriteOptions
          };

          if (hasSelection) {
            callOptions.selfContext = this._quill.getText();
          }

          const improved = await gbc.AIService.rewrite(text, callOptions);

          if (!improved || this._stopRewriteRequested) {
            this._aiBanner.hide();
            return;
          }
          this._aiPendingRewrite = {
            text: improved,
            range: hasSelection ? { index: range.index, length: range.length } : null,
            rewriteOptions: rewriteOptions ? { ...rewriteOptions } : {}
          };
          this._setAIBannerConfirm();
        } finally {
          this.removeClass("gbc_aiLoading");

          if (this._aiPendingRewrite) {
            this.addClass("gbc_aiRewritePendingState");
          } else {
            this._clearRewriteTargetDecoration();
          }

          this._rewriteInProgress = false;
        }
      },

      /**
       * Toggle the AI rewrite action dropdown
       * @private
       */
      _handleRewriteActions: function () {
        this._ensureRewriteActionsDropDown();

        if (!this._rewriteActionsBtn) {
          return;
        }

        // Advertise the popup linkage to assistive tech (the rewrite actions popup is a menu).
        // Done here (rather than on Quill button creation) because the dropdown is lazy-created.
        cls.DOMElementHelper.updateAttribute(this._rewriteActionsBtn, "aria-haspopup", "menu");
        cls.DOMElementHelper.updateAttribute(this._rewriteActionsBtn, "aria-controls", this._rewriteActionsDropDown.getRootClassName());

        this._rewriteActionsDropDown.parentElement = this._rewriteActionsBtn;
        this._rewriteActionsDropDown.x = null;
        this._rewriteActionsDropDown.y = null;
        this._rewriteActionsDropDown.toggle();
      },

      /**
       * Lazy-create the AI floating banner and its actions/content
       * @private
       */
      _ensureAIBanner: function () {
        if (this._aiBanner) {
          return;
        }

        this._aiBanner = cls.WidgetFactory.createWidget("FloatingBanner", this.getBuildParameters());
        this._aiBanner.setBackdrop(false);
        this._aiBanner.addClass("gbc_FloatingBannerWidget__AIRewrite");
        document.body.appendChild(this._aiBanner.getElement());

        this._aiStopItem = this._createAIBannerItem(
          "zmdi-stop-circle-outline",
          gbc.I18NService.t("gwc.main.richtext.ai.stopButton"),
          this._stopRewrite.bind(this)
        );
        this._aiApplyItem = this._createAIBannerItem(
          "zmdi-check",
          gbc.I18NService.t("gwc.main.richtext.ai.applyButton"),
          this._applyPendingRewrite.bind(this)
        );
        this._aiRefreshItem = this._createAIBannerItem(
          "zmdi-refresh",
          gbc.I18NService.t("gwc.main.richtext.ai.refreshButton"),
          this._refreshRewrite.bind(this)
        );
        this._aiCancelItem = this._createAIBannerItem(
          "zmdi-close",
          gbc.I18NService.t("gwc.main.richtext.ai.cancelButton"),
          this._cancelPendingRewrite.bind(this)
        );

        this._aiLoadingContent = document.createElement("div");
        this._aiLoadingContent.className = "gbc_aiBannerLoading";
        const loadingText = document.createElement("span");
        loadingText.textContent = gbc.I18NService.t("gwc.main.richtext.ai.loadingTitle");
        this._aiLoadingContent.append(loadingText, this._aiStopItem.getElement());

        this._aiConfirmHeaderContent = document.createElement("div");
        this._aiConfirmHeaderContent.className = "gbc_aiBannerConfirmHeader";
        const label = document.createElement("span");
        label.className = "gbc_aiBannerLabel";
        label.textContent = gbc.I18NService.t("gwc.main.richtext.ai.improvedVersionTitle");
        const actions = document.createElement("div");
        actions.className = "gbc_aiBannerActions";
        actions.append(this._aiApplyItem.getElement(), this._aiRefreshItem.getElement(), this._aiCancelItem.getElement());
        const aiDisclaimerLabel = document.createElement("span");
        aiDisclaimerLabel.className = "gbc_aiBannerDisclaimer";
        aiDisclaimerLabel.textContent = gbc.I18NService.t("gwc.main.richtext.ai.disclaimer");

        this._aiConfirmTextEdit = cls.WidgetFactory.createWidget("TextEdit", this.getBuildParameters());
        this._aiConfirmTextEdit.addClass("gbc_aiConfirmTextEdit");
        this._aiConfirmTextEdit.setRows(8);
        this._aiConfirmHeaderContent.append(label, actions);
        this._aiConfirmContent = document.createElement("div");
        this._aiConfirmContent.className = "gbc_aiBannerConfirm";
        this._aiConfirmContent.append(this._aiConfirmTextEdit.getElement(), aiDisclaimerLabel);
      },

      /**
       * Create a floating banner action item
       * @param {string} icon
       * @param {string} title
       * @param {Function} handler
       * @returns {classes.ItemWidget}
       * @private
       */
      _createAIBannerItem: function (icon, title, handler) {
        const item = cls.WidgetFactory.createWidget("Item", this.getBuildParameters());
        item.setPrimaryIcon(icon);
        item.setTitle(title);
        item.onClick(handler);
        return item;
      },

      /**
       * Show the banner in "loading" state
       * @private
       */
      _setAIBannerLoading: function () {
        this._ensureAIBanner();
        this._aiBanner.removeClass("gbc_aiRewriteConfirmState");
        this._aiBanner.setBackdrop(true, true);
        this._aiBanner.setIcon("zmdi-auto-fix");
        this._aiBanner.setHeaderContent(this._aiLoadingContent);
        this._aiBanner.setContent();
        this._aiBanner.setPositionFromElement(this._toolbarElement, "bottom");
        this._aiBanner.setSwipeEnabled(false);
        this._aiBanner.setIsAIWidget(true);
        this._aiBanner.show();
      },

      /**
       * Show the banner in "confirm" state
       * @private
       */
      _setAIBannerConfirm: function () {
        this._ensureAIBanner();
        this._aiBanner.addClass("gbc_aiRewriteConfirmState");
        this._aiBanner.setBackdrop(true, true);
        this._aiConfirmTextEdit.setEnabled(true);
        this._aiConfirmTextEdit.setValue(this._aiPendingRewrite.text);
        this._aiBanner.setIcon("zmdi-creation");
        this._aiBanner.setHeaderContent(this._aiConfirmHeaderContent);
        this._aiBanner.setContent(this._aiConfirmContent);
        this._aiBanner.setPositionFromElement(this._toolbarElement, "bottom");
        this._aiBanner.setSwipeEnabled(true);
        this._aiBanner.domFocus();
        this._aiBanner.setIsAIWidget(true);
        this._aiBanner.show();
      },

      /**
       * Apply the pending rewrite (from textEdit in floatingBanner) to the richText
       * @private
       */
      _applyPendingRewrite: function () {
        if (!this._aiPendingRewrite) {
          return;
        }

        this._clearRewriteTargetDecoration();

        const improved = this._aiConfirmTextEdit.getInputElement().value;
        const range = this._aiPendingRewrite.range;

        if (range && range.length > 0) {
          this._quill.deleteText(range.index, range.length);
          this._quill.insertText(range.index, improved);
        } else {
          this._quill.setText(improved);
        }

        this._aiPendingRewrite = null;
        this._aiBanner.removeClass("gbc_aiRewriteConfirmState");
        this._aiBanner.hide();
      },

      /**
       * Cancel the pending rewrite
       * @private
       */
      _cancelPendingRewrite: function () {
        this._clearRewriteTargetDecoration();
        this._aiPendingRewrite = null;
        this._aiBanner.removeClass("gbc_aiRewriteConfirmState");
        this._aiBanner.hide();
      },

      /**
       * Refresh the rewrite by running it again
       * @private
       */
      _refreshRewrite: function () {
        const pendingRewrite = this._aiPendingRewrite;

        this._clearRewriteTargetDecoration();
        this._aiPendingRewrite = null;
        this._aiBanner.removeClass("gbc_aiRewriteConfirmState");
        this._aiBanner.hide();

        if (pendingRewrite) {
          this._handleRewrite(pendingRewrite.rewriteOptions, pendingRewrite.range);
        } else {
          this._handleRewrite();
        }
      },

      /**
       * Request cancellation of the current rewrite
       * @private
       */
      _stopRewrite: function () {
        if (!this._rewriteInProgress) {
          return;
        }
        this._stopRewriteRequested = true;
        this._aiBanner.removeClass("gbc_aiRewriteConfirmState");
        this._aiBanner.hide();
      },

      /**
       * Lazy-create the rewrite actions dropdown
       * @private
       */
      _ensureRewriteActionsDropDown: function () {
        if (this._rewriteActionsDropDown) {
          return;
        }

        this._rewriteActionsDropDown = cls.WidgetFactory.createWidget("ChoiceDropDown", this.getBuildParameters());
        // popup hosts AI action menuitems
        this._rewriteActionsDropDown.setAriaRole("menu");
        this._rewriteActionsDropDown.setParentWidget(this);
        this._rewriteActionsDropDown.setCanOverlay(true);

        const syncAriaExpanded = (expanded) => {
          if (this._rewriteActionsBtn) {
            cls.DOMElementHelper.updateAttribute(this._rewriteActionsBtn, "aria-expanded", String(expanded));
          }
        };
        this._rewriteActionsDropDown.onOpen(() => syncAriaExpanded(true));
        this._rewriteActionsDropDown.onClose(() => syncAriaExpanded(false));

        this._rewriteActionsDropDown.shouldClose = function (targetElement) {
          return !cls.DOMElementHelper.isElementOrChildOf(targetElement, this.getElement());
        }.bind(this);

        const addChoice = (label, icon, onSelect) => {
          const item = cls.WidgetFactory.createWidget("Item", this.getBuildParameters());
          item.setText(label);
          item.setPrimaryIcon(icon);
          item.addClass("gbc_ItemWidget_aiRewriter");
          this._rewriteActionsDropDown.addChildWidget(item, {
            clickCallback: onSelect
          });
        };

        addChoice(gbc.I18NService.t("gwc.main.richtext.ai.rewrite"), "zmdi-auto-fix", () => {
          this._handleRewrite();
        });
        addChoice(gbc.I18NService.t("gwc.main.richtext.ai.makeToneProfessional"), "zmdi-feather", () => {
          this._handleRewrite({ tone: "more-formal" });
        });
        addChoice(gbc.I18NService.t("gwc.main.richtext.ai.makeToneCasual"), "zmdi-feather", () => {
          this._handleRewrite({ tone: "more-casual" });
        });
        addChoice(gbc.I18NService.t("gwc.main.richtext.ai.makeShorter"), "zmdi-text-short", () => {
          this._handleRewrite({ length: "shorter" });
        });
        addChoice(gbc.I18NService.t("gwc.main.richtext.ai.makeLonger"), "zmdi-text-long", () => {
          this._handleRewrite({ length: "longer" });
        });
      },

      /**
       * Register once the Quill inline format used to mark the rewrite target range
       * Done lazily here (not at file top) to keep initialization local to RichTextWidget
       * @private
       */
      _ensureAIRewriteTargetAttributor: function () {
        if (cls.RichTextWidget._aiRewriteTargetAttributorRegistered) {
          return;
        }

        const Parchment = Quill.import("parchment");
        const aiRewriteTargetClass = new Parchment.ClassAttributor("aiRewriteTarget", "gbc_aiRewriteTargetText", {
          scope: Parchment.Scope.INLINE
        });

        Quill.register(aiRewriteTargetClass, true);
        cls.RichTextWidget._aiRewriteTargetAttributorRegistered = true;
      },

      /**
       * Remove rewrite target styling and clear the stored target range
       * @private
       */
      _clearRewriteTargetDecoration: function () {
        this.removeClass("gbc_aiRewritePendingState");
        if (this._rewriteTargetRange) {
          this._quill.formatText(this._rewriteTargetRange.index, this._rewriteTargetRange.length, "aiRewriteTarget", false, "silent");
          this._rewriteTargetRange = null;
        }
      }
    };
  });
  cls.WidgetFactory.registerBuilder("RichText", cls.RichTextWidget);
});
